import React, { useContext, useEffect, useRef, useState } from 'react';
import useClickOutside from '../hooks/useClickOutside';
import {
  createNewInterviewPrimary,
  fetchInterviewCountryDetails,
  fetchInterviewProductDetails,
  fetchInterviewProjectDetails,
  fetchInterviewVenueDetails,
  fetchUploadedFileDetails,
  trackUserClicks,
  validateTranscriptInput,
  getValidToken,
} from '../utils';
import { APIContext } from '../contexts/APIContext';
import MultiSelectDropdown from './ui/MultiSelectDropdown';
import { useToast } from './ui/toast/UseToast';
import BounceLoader from './ui/Loaders/BounceLoader';
import { useInputHandler } from '../hooks/useInputHandler';

type ResearchType = 'Primary' | 'Secondary';

type Project = {
  project_id: number;
  project_name: string;
};

interface PrimaryProps {
  researchType: ResearchType;
  setResearchType: (type: ResearchType) => void;
}

type FormData = {
  selectedProject: string;
  interviewDate: string;
  companyName: string;
  jobTitle: string;
  transcript: string;
  selectedCountries: string[];
  selectedVenues: string[];
  selectedProducts: string[];
};

const NewInterviewPrimary = ({ researchType, setResearchType }: PrimaryProps) => {
  const {
    websocketConnected,
    websocketConnectionRef: { current: websocket },
    interviewProjectNames,
    interviewCountryNames,
    interviewVenueNames,
    interviewProductNames,
    accounts,
    newInterviewStatus,
    setNewInterviewStatus,
    fileDetails,
    newProjectStatus,
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
  } = useContext(APIContext);
  const [showOptionalInformation, setShowOptionalInformation] = useState<boolean>(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState<boolean>(false);
  const projectDropdownRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [interviewDate, setInterviewDate] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [transcript, setTranscript] = useState('');
  const [transcriptError, setTranscriptError] = useState('');
  const [reviewModePrimary, setReviewModePrimary] = useState(false);
  const [primaryFormData, setPrimaryFormData] = useState<FormData>({
    selectedProject: '',
    interviewDate: '',
    companyName: '',
    jobTitle: '',
    transcript: '',
    selectedCountries: [],
    selectedVenues: [],
    selectedProducts: [],
  });
  const [showLoader, setShowLoader] = useState(false);
  const { toast } = useToast();

  useClickOutside(projectDropdownRef, () => {
    setShowProjectDropdown(false);
  });

  useEffect(() => {
    (async () => {
      const token = await getValidToken();
      if (!websocket || websocket.state !== 'Connected') return;
      // if (!interviewProjectNames.length) {
      fetchInterviewProjectDetails(accounts, websocket, token);
      // }

      if (!interviewCountryNames.length) {
        fetchInterviewCountryDetails(accounts, websocket, token);
      }

      if (!interviewVenueNames.length) {
        fetchInterviewVenueDetails(accounts, websocket, token);
      }

      if (!interviewProductNames.length) {
        fetchInterviewProductDetails(accounts, websocket, token);
      }

      if (!fileDetails.length) {
        fetchUploadedFileDetails(accounts, websocket, token);
      }
    })();
  }, [websocket, websocketConnected, newProjectStatus]);

  useEffect(() => {
    if (!newInterviewStatus) return;

    if (newInterviewStatus === 'success') {
      toast({
        status: 'success',
        description: 'Transcript uploaded successfully',
      });

      setSelectedProject('');
      setProjectSearch('');
      setInterviewDate('');
      setCompanyName('');
      setJobTitle('');
      setTranscript('');
      setSelectedCountries([]);
      setSelectedVenues([]);
      setSelectedProducts([]);

      scrollToTop();
      setReviewModePrimary(false);
      setShowLoader(false);
    } else {
      toast({
        status: 'info',
        description: 'Failed to upload Transcript',
      });

      setShowLoader(false);
    }

    setNewInterviewStatus('');
  }, [newInterviewStatus]);

  useEffect(() => {
    if (reviewModePrimary) {
      const areas = document.querySelectorAll('.-reviewmode textarea');

      areas.forEach((t: any) => {
        // set initial height
        t.style.height = 'auto';
        t.style.height = t.scrollHeight + 'px';
        t.style.overflow = 'hidden';
      });
    }
    return () => {
      const areas = document.querySelectorAll('.expand-class textarea');
      // runs when component unmounts or before next effect
      areas.forEach((t: any) => {
        t.style.height = '18.25rem';
        t.style.overflow = '';
      });
    };
  }, [reviewModePrimary]);

  const filteredProjects =
    interviewProjectNames &&
    interviewProjectNames.filter((p: Project) => p.project_name.toLowerCase().includes(projectSearch.toLowerCase()));

  const handleSelectProject = (name: string) => {
    // setReviewModePrimary(true);
    setSelectedProject(name);
    setProjectSearch(name);
    setShowProjectDropdown(false);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // setReviewModePrimary(true);
    if (!value) {
      setInterviewDate('');
      return;
    }
    setInterviewDate(value);
  };

  const textRegex = /^[a-zA-Z0-9 ._-]*$/;

  const companyHandlers = useInputHandler(textRegex, setCompanyName, setReviewModePrimary);
  const jobHandlers = useInputHandler(textRegex, setJobTitle, setReviewModePrimary);

  const handleTranscriptInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // setReviewModePrimary(true);

    setTranscript(newValue);

    if (validateTranscriptInput(newValue)) {
      setTranscriptError(`Html tags are not allowed`);
    } else {
      setTranscriptError('');
    }
  };

  const handleTranscriptBlur = () => {
    const trimmed = transcript.trim();

    if (transcript !== trimmed) {
      setTranscript(trimmed);
    }

    if (validateTranscriptInput(trimmed)) {
      setTranscriptError(`Html tags are not allowed`);
    }
  };

  const handleReview = () => {
    setPrimaryFormData({
      selectedProject: selectedProject,
      interviewDate: interviewDate,
      companyName: companyName,
      jobTitle: jobTitle,
      transcript: transcript,
      selectedCountries: selectedCountries,
      selectedVenues: selectedVenues,
      selectedProducts: selectedProducts,
    });
    setReviewModePrimary(true);
    scrollToTop();
  };

  const handleSubmit = async () => {
    const token = await getValidToken();
    setShowLoader(true);
    createNewInterviewPrimary(accounts, websocket, primaryFormData, token);
    trackUserClicks(websocket, 'PrimarySubmit', userDetails, chatSessionId, WBUId, AppName, accounts, token);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <div className="c-tab-content">
        <div className="c-tab-content__content">
          <div className="c-tab-content__input-wrap">
            <label>Research Type*</label>
            <div className="c-tab-content__radio-btn">
              <label className="o-radio ">
                <input
                  type="radio"
                  name="primary"
                  id="primary"
                  value="Primary"
                  checked={researchType === 'Primary'}
                  onChange={() => setResearchType('Primary')}
                />
                <label htmlFor="primary">Primary</label>
              </label>
              <label className="o-radio ">
                <input
                  type="radio"
                  name="primary"
                  id="secondary"
                  value="Secondary"
                  checked={researchType === 'Secondary'}
                  onChange={() => setResearchType('Secondary')}
                />
                <label htmlFor="secondary">Secondary</label>
              </label>
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap">
              <label>Project*</label>
              <div className={`c-tab-content__input-text ${reviewModePrimary ? '-reviewmode' : ''}`}>
                <div className="o-multi-select-two " tabIndex={0} ref={projectDropdownRef}>
                  <div className={`o-multi-select-two__head ${showProjectDropdown ? '-active' : ''}`}>
                    <div className="o-input-wrap ">
                      <i className="icon-search"></i>
                      <input
                        type="text"
                        className="o-input -dropdown"
                        id="input1"
                        name="input1"
                        placeholder="Search & Select Project..."
                        value={projectSearch}
                        onChange={(e) => {
                          // setReviewModePrimary(true);
                          const value = e.target.value;
                          setProjectSearch(value);
                          setSelectedProject(value);
                        }}
                        onClick={() => setShowProjectDropdown(true)}
                      />
                    </div>
                    <i
                      className={`o-multi-select-two__arrow icon-caret ${showProjectDropdown ? '-active' : ''}`}
                      tabIndex={0}
                      onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                    ></i>
                  </div>
                  <div className={`o-multi-select-two__dropdown ${showProjectDropdown ? '-active' : ''}`}>
                    <ul className="o-multi-select-two__list">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((p: Project) => (
                          <a
                            key={p.project_id}
                            role="option"
                            className="c-dropdown-item"
                            onClick={() => handleSelectProject(p.project_name)}
                            style={{ cursor: 'pointer' }}
                          >
                            {p.project_name}
                          </a>
                        ))
                      ) : (
                        <li className="c-dropdown-item disabled">No results found</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="c-tab-content__input-wrap">
              <label> Interview Date*</label>
              <div className={`c-tab-content__input-date ${reviewModePrimary ? '-reviewmode' : ''} `}>
                <div className="o-input-wrap ">
                  <input
                    type="date"
                    placeholder="mm/dd/yyyy"
                    onChange={handleDateChange}
                    className={`o-input ${interviewDate === '' ? 'no-date' : ''}`}
                    value={interviewDate}
                    style={{ paddingBottom: '10px' }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap">
              <label>Company*</label>
              <div className={`o-input-wrap ${reviewModePrimary ? '-reviewmode' : ''}`}>
                <input
                  type="text"
                  className="o-input"
                  id="input1"
                  name="input1"
                  placeholder="Add Company"
                  maxLength={250}
                  value={companyName}
                  {...companyHandlers}
                />
              </div>
            </div>
            <div className="c-tab-content__input-wrap">
              <label>Job Title*</label>
              <div className={`o-input-wrap ${reviewModePrimary ? '-reviewmode' : ''}`}>
                <input
                  type="text"
                  className="o-input"
                  id="input1"
                  name="input1"
                  placeholder="Add Job Title"
                  maxLength={250}
                  value={jobTitle}
                  {...jobHandlers}
                />
              </div>{' '}
            </div>
          </div>
          <div className="c-tab-content__input">
            <div className="c-tab-content__input-wrap -textarea">
              <label>Interview Transcript*</label>
              <div className={`o-text-area expand-class ${reviewModePrimary ? '-reviewmode' : ''}`}>
                <textarea
                  className="o-text-area__input"
                  placeholder="Write interview notes here"
                  maxLength={100000}
                  value={transcript}
                  // onKeyDown={handleKeyPress}
                  onChange={handleTranscriptInput}
                  onBlur={handleTranscriptBlur}
                  style={{ height: '18.25rem' }}
                ></textarea>
                <span className="o-text-area__total-count" style={{ marginTop: '-.9rem' }}>
                  {transcript.length.toLocaleString()} / 100,000
                </span>
                {transcriptError && (
                  <span className="o-text-area__error" style={{ bottom: '.1rem' }}>
                    {transcriptError}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="c-tab-content__input-expand">
            <a
              className="c-tab-content__accordion c-accordion__control"
              href="javascript:;"
              role="button"
              aria-expanded={showOptionalInformation}
              aria-controls="region1"
              onClick={() => {
                setShowOptionalInformation(!showOptionalInformation);
              }}
            >
              Optional Information
            </a>
            <div
              id="region1"
              className={`c-accordion__collapse ${showOptionalInformation ? '-show' : ''}`}
              aria-hidden={!showOptionalInformation}
              data-parentid="accordionParent"
            >
              <div className="c-tab-content__input">
                <MultiSelectDropdown
                  label="Country"
                  options={interviewCountryNames}
                  selected={selectedCountries}
                  setSelected={setSelectedCountries}
                  placeholder="Search & Select countries..."
                  review={reviewModePrimary}
                  setReview={setReviewModePrimary}
                />
                <MultiSelectDropdown
                  label="Venue"
                  options={interviewVenueNames}
                  selected={selectedVenues}
                  setSelected={setSelectedVenues}
                  placeholder="Search & Select venues..."
                  review={reviewModePrimary}
                  setReview={setReviewModePrimary}
                />
              </div>

              <div className="c-tab-content__input">
                <MultiSelectDropdown
                  label="Product"
                  options={interviewProductNames}
                  selected={selectedProducts}
                  setSelected={setSelectedProducts}
                  placeholder="Search & Select products..."
                  review={reviewModePrimary}
                  setReview={setReviewModePrimary}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {!reviewModePrimary ? (
        <div className="c-stickyfooter__wrap  -review" style={{ position: 'fixed', zIndex: 2 }}>
          <div className="app-container c-stickyfooter__inner">
            <div className="c-stickyfooter__right">
              <button
                type="button"
                className="o-primary-button "
                aria-label="Submit"
                disabled={
                  selectedProject && interviewDate && companyName && jobTitle && transcript && !transcriptError
                    ? false
                    : true
                }
                onClick={handleReview}
              >
                Review
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="c-stickyfooter__wrap  -review" style={{ position: 'fixed', zIndex: 2 }}>
          <div className="app-container c-stickyfooter__inner">
            <div className="c-stickyfooter__right">
              <button
                type="button"
                className="o-secondary-button "
                aria-label="Edit"
                onClick={() => setReviewModePrimary(false)}
              >
                Edit
              </button>
              <button type="button" className="o-primary-button " aria-label="Submit" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
      {showLoader && (
        <div className="fixed inset-0 flex flex-col justify-center items-center bg-[#f1f1f180] backdrop-blur-[1px] z-[9999]">
          <BounceLoader loading={true} size={60} />
          <p className="mt-6 text-lg">One Moment Please...</p>
        </div>
      )}
    </>
  );
};

export default NewInterviewPrimary;
